import React, { useEffect, useState, useRef } from 'react';
import 'regenerator-runtime/runtime';
import { Box, Typography, Button, Container, Grid, CircularProgress, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { makeProxyRequest } from '../../utils/api';
import { useAuth } from '../../hooks/useAuth';

interface ConversationData {
    name: string;
    role: string;
    conversation_id: string;
    conversation_url: string;
    status: string;
    error?: string;
}

const BoardRoom = () => {
    const [conversations, setConversations] = useState<ConversationData[]>([]);
    const [isLoading, setIsLoading] = useState(false); // Changed to false - will start after guest entry
    const [error, setError] = useState<string | null>(null);
    const [activeTarget, setActiveTarget] = useState<string | null>(null);
    const [micPermission, setMicPermission] = useState(false);
    const [conversationLog, setConversationLog] = useState<{ speaker: string, text: string, time: string }[]>([]);
    const { authUser } = useAuth();

    // Guest Mode State
    const [guestInfo, setGuestInfo] = useState<{ name: string, title: string, agenda: string } | null>(null);
    const [showGuestForm, setShowGuestForm] = useState(true);
    // PREFILLED for testing
    const [guestFormData, setGuestFormData] = useState({
        name: 'Alex Chen',
        title: 'Investor',
        agenda: 'Q1 Review and AI Investment Strategy'
    });

    // Turn-Taking State
    const [lastSpeakTime, setLastSpeakTime] = useState<number>(0);
    const [isWaitingResponse, setIsWaitingResponse] = useState(false);
    const TURN_COOLDOWN_MS = 8000; // 8 second cooldown

    // Meeting Timer
    const [meetingStartTime, setMeetingStartTime] = useState<number | null>(null);
    const [elapsedTime, setElapsedTime] = useState('00:00');

    // Speech Recognition Setup
    const commands = [
        {
            command: ['Sabrina *', 'hello Sabrina', 'hey Sabrina'],
            callback: () => setActiveTarget('Sabrina'),
            isFuzzyMatch: true,
            bestMatchOnly: true
        },
        {
            command: ['Patrick *', 'hello Patrick', 'hey Patrick'],
            callback: () => setActiveTarget('Patrick'),
            isFuzzyMatch: true,
            bestMatchOnly: true
        },
        {
            command: ['Lucy *', 'hello Lucy', 'hey Lucy'],
            callback: () => setActiveTarget('Lucy'),
            isFuzzyMatch: true,
            bestMatchOnly: true
        },
        {
            command: ['Rose *', 'hello Rose', 'hey Rose'],
            callback: () => setActiveTarget('Rose'),
            isFuzzyMatch: true,
            bestMatchOnly: true
        },
        {
            command: ['Benjamin *', 'hello Benjamin', 'hey Benjamin'],
            callback: () => setActiveTarget('Benjamin'),
            isFuzzyMatch: true,
            bestMatchOnly: true
        },
        {
            command: ['everyone', 'all of you'],
            callback: () => setActiveTarget(null)
            // Requirement: "listening" -> means they hear you.
            // If we mute local audio, they don't hear us.
            // Requirement: "Once answer/respond when they are called... otherwise they are listening" implies they are ALWAYS listening but only respond when called?
            // Actually, usually with Tavus/LLM, if they hear audio, they will try to respond.
            // To prevent them from responding, we must MUTE our mic to them.
            // So "listening" in user request might mean "passive", but implementation-wise we must MUTE mic to prevent unwanted replies.
            // "Otherwise they are listening" -> could mean they are aware, but effectively we want them to NOT respond.
            // The only way to ensure they don't respond to background noise or others talking is to mute the mic sent to them.
        }
    ];

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition({ commands });

    // Auto-start listening loop
    useEffect(() => {
        if (micPermission && !listening) {
            SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
        }
    }, [micPermission, listening]);


    // Meeting Timer Effect
    useEffect(() => {
        if (!meetingStartTime) return;
        const timer = setInterval(() => {
            const elapsed = Date.now() - meetingStartTime;
            const mins = Math.floor(elapsed / 60000);
            const secs = Math.floor((elapsed % 60000) / 1000);
            setElapsedTime(`${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
        }, 1000);
        return () => clearInterval(timer);
    }, [meetingStartTime]);

    // Guest Form Submit Handler
    const handleGuestSubmit = async () => {
        if (!guestFormData.name.trim()) {
            setError('Please enter your name');
            return;
        }

        setGuestInfo(guestFormData);
        setShowGuestForm(false);
        setIsLoading(true);
        setError(null);

        try {
            // Request Mic Permission
            await navigator.mediaDevices.getUserMedia({ audio: true });
            setMicPermission(true);

            // Use authUser if available, otherwise generate guest ID
            const userId = authUser?.uid || `guest_${Date.now()}`;

            const response = await makeProxyRequest(
                'api/board_room/start_meeting',
                'POST',
                {
                    user_id: userId,
                    guest_name: guestFormData.name,
                    guest_title: guestFormData.title,
                    meeting_agenda: guestFormData.agenda
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    throw new Error(errorJson.detail || errorJson.message || "Failed to start meeting");
                } catch (e) {
                    throw new Error(`Failed to start meeting: ${response.status} ${response.statusText} - ${errorText}`);
                }
            }

            const data = await response.json();
            setConversations(data.conversations);
            setMeetingStartTime(Date.now());
        } catch (err: any) {
            console.error(err);
            setError(err.message || "Unknown error");
        } finally {
            setIsLoading(false);
        }
    };

    // Name detection - match names anywhere in transcript (more flexible)
    useEffect(() => {
        if (!transcript.trim()) return;

        const lowerTrans = transcript.toLowerCase().trim();
        const boardNames = ['sabrina', 'patrick', 'lucy', 'rose', 'benjamin'];

        // Find first mentioned name (word boundary match)
        for (const name of boardNames) {
            // Match name as a word (not substring)
            const namePattern = new RegExp(`\\b${name}\\b`, 'i');
            if (namePattern.test(lowerTrans)) {
                const properName = name.charAt(0).toUpperCase() + name.slice(1);
                if (activeTarget !== properName) {
                    console.log(`[BoardRoom] ✅ Detected target: ${properName} (from: "${lowerTrans.slice(0, 50)}...")`);
                    setActiveTarget(properName);
                    // Reset transcript after detection to avoid re-triggering
                    resetTranscript();
                }
                break;
            }
        }

        // Log user speech to console
        if (transcript.trim()) {
            console.log('[User Speaking]:', transcript);
        }

        // Reset transcript periodically to avoid huge buffer
        // Also save to conversation log before reset
        if (transcript.length > 200) {
            const time = new Date().toLocaleTimeString();
            setConversationLog(prev => [...prev, { speaker: 'You', text: transcript, time }]);
            resetTranscript();
        }

    }, [transcript, resetTranscript, activeTarget]);


    // Sync Mic changes to iframes
    useEffect(() => {
        // Broadcast active target to all iframes
        console.log('[BoardRoom] Broadcasting activeTarget:', activeTarget);
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach((iframe) => {
            // We use data-name to identify which avatar this frame belongs to
            const frameName = iframe.getAttribute('data-name');
            if (frameName && iframe.contentWindow) {
                const shouldEnableMic = activeTarget === "Everyone" || activeTarget === frameName;
                console.log(`[BoardRoom] Sending to ${frameName}: enabled=${shouldEnableMic}`);
                iframe.contentWindow.postMessage({ type: 'SET_MIC', enabled: shouldEnableMic }, window.location.origin);
            }
        });
    }, [activeTarget]);

    if (!browserSupportsSpeechRecognition) {
        return <span>Browser doesn't support speech recognition. Use Chrome.</span>;
    }

    return (
        <Box sx={{ height: '100vh', width: '100vw', bgcolor: '#1a1a1a', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Guest Entry Dialog */}
            <Dialog open={showGuestForm} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#1a1a1a', color: 'white' }}>
                    Welcome to the Board Room
                </DialogTitle>
                <DialogContent sx={{ bgcolor: '#1a1a1a' }}>
                    <Typography variant="body2" color="#888" sx={{ mb: 3 }}>
                        You're about to join a meeting with our executive team. Please introduce yourself.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Your Name"
                        value={guestFormData.name}
                        onChange={(e) => setGuestFormData(prev => ({ ...prev, name: e.target.value }))}
                        sx={{ mb: 2, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: '#888' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#444' } } }}
                        required
                    />
                    <TextField
                        fullWidth
                        label="Your Title (optional)"
                        value={guestFormData.title}
                        onChange={(e) => setGuestFormData(prev => ({ ...prev, title: e.target.value }))}
                        sx={{ mb: 2, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: '#888' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#444' } } }}
                        placeholder="e.g., Investor, Consultant, Board Member"
                    />
                    <TextField
                        fullWidth
                        label="Meeting Agenda (optional)"
                        value={guestFormData.agenda}
                        onChange={(e) => setGuestFormData(prev => ({ ...prev, agenda: e.target.value }))}
                        sx={{ mb: 2, '& .MuiInputBase-input': { color: 'white' }, '& .MuiInputLabel-root': { color: '#888' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#444' } } }}
                        placeholder="e.g., Q1 Strategy Review, Partnership Discussion"
                        multiline
                        rows={2}
                    />
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                </DialogContent>
                <DialogActions sx={{ bgcolor: '#1a1a1a', p: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGuestSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={20} /> : 'Join Meeting'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Header with Timer and Status */}
            <Box sx={{ p: 2, borderBottom: '1px solid #333', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h5" color="white">Board Room</Typography>
                    {meetingStartTime && (
                        <Typography variant="body2" color="#00ff00" sx={{ bgcolor: '#222', px: 1, py: 0.5, borderRadius: 1 }}>
                            ⏱️ {elapsedTime}
                        </Typography>
                    )}
                    {guestInfo && (
                        <Typography variant="body2" color="#888">
                            Welcome, <span style={{ color: '#00bcd4' }}>{guestInfo.name}</span>
                            {guestInfo.title && <span style={{ color: '#666' }}> ({guestInfo.title})</span>}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        color={micPermission ? "success" : "error"}
                        onClick={() => setActiveTarget(activeTarget ? null : "Everyone")}
                        disabled={!guestInfo}
                    >
                        {activeTarget ? "Mute All" : "Unmute All (Talk to Everyone)"}
                    </Button>

                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="#888">
                            talking to: <span style={{ color: '#00ff00', fontWeight: 'bold' }}>{activeTarget || "None"}</span>
                        </Typography>
                        {isWaitingResponse && (
                            <Typography variant="caption" color="#ff9800">
                                ⏳ Waiting for {activeTarget} to respond...
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Quick Access Buttons */}
            <Box sx={{ p: 1, display: 'flex', gap: 1, overflowX: 'auto', borderBottom: '1px solid #333' }}>
                {['Sabrina', 'Patrick', 'Lucy', 'Rose', 'Benjamin'].map(name => (
                    <Button
                        key={name}
                        variant={activeTarget === name ? "contained" : "outlined"}
                        size="small"
                        onClick={() => setActiveTarget(name === activeTarget ? null : name)}
                    >
                        {name}
                    </Button>
                ))}
                <Button
                    variant={activeTarget === "Everyone" ? "contained" : "outlined"}
                    size="small"
                    onClick={() => setActiveTarget("Everyone")}
                >
                    All
                </Button>
            </Box>

            {
                isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }} color="white">Assembling the Board...</Typography>
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Grid container spacing={2} sx={{ p: 2, flexGrow: 1 }}>
                        {conversations.map((member) => (
                            <Grid item xs={12} sm={6} md={4} key={member.name} sx={{ height: '50%' }}>
                                {member.conversation_url ? (
                                    <Box sx={{
                                        width: '100%',
                                        height: '100%',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        bgcolor: '#000',
                                        border: activeTarget === member.name ? '3px solid #00ff00' : '3px solid transparent',
                                        position: 'relative'
                                    }}>
                                        {/* Active indicator badge */}
                                        {activeTarget === member.name && (
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: '#00ff00',
                                                color: '#000',
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 1,
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                zIndex: 10
                                            }}>
                                                🎤 SPEAKING TO
                                            </Box>
                                        )}
                                        <iframe
                                            src={`/board-room/avatar?url=${encodeURIComponent(member.conversation_url)}&name=${encodeURIComponent(member.name)}&role=${encodeURIComponent(member.role)}`}
                                            style={{ border: 'none', width: '100%', height: '100%' }}
                                            data-name={member.name}
                                            title={`Avatar - ${member.name}`}
                                            allow="microphone; camera; autoplay"
                                        />
                                    </Box>
                                ) : (
                                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#000', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                        <Typography color="error" align="center">Failed to load {member.name}</Typography>
                                        {member.error && (
                                            <Typography variant="caption" color="white" align="center" sx={{ mt: 1, wordBreak: 'break-word' }}>
                                                {member.error}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        ))}
                    </Grid>
                )
            }

            {/* Transcript Panel */}
            <Box sx={{
                p: 2,
                borderTop: '1px solid #333',
                bgcolor: '#111',
                maxHeight: '150px',
                overflowY: 'auto'
            }}>
                <Typography variant="subtitle2" color="#00ff00" sx={{ mb: 1 }}>
                    🎤 Live Transcript
                </Typography>
                <Typography variant="body2" color="white" sx={{ mb: 1, fontStyle: 'italic' }}>
                    You: {transcript || '(listening...)'}
                </Typography>
                {conversationLog.length > 0 && (
                    <Box sx={{ mt: 1, borderTop: '1px solid #333', pt: 1 }}>
                        <Typography variant="caption" color="#888">History:</Typography>
                        {conversationLog.slice(-5).map((entry, i) => (
                            <Typography key={i} variant="caption" color="#aaa" sx={{ display: 'block' }}>
                                [{entry.time}] {entry.speaker}: {entry.text}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default BoardRoom;
